import "./redux/axiosSetup";
import { useEffect, useRef, useState } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { createRoot } from "react-dom/client";

import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useOutlet,
  useNavigate,
  redirect,
} from "react-router-dom";

import Login from "./containers/pages/login";
import Home from "./containers/pages/home";
import AppHeader from "./components/navigation/AppHeader.jsx";
import PacientesPage from "./containers/pages/PacientesPage.jsx";
import CrearPacientePage from "./containers/pages/CrearPacientePage.jsx";
import PacienteDetallePage from "./containers/pages/PacienteDetallePage.jsx";
import AgendaPage from "./containers/pages/AgendaPage.jsx";
import CargosPage from "./containers/pages/CargosPage.jsx";
import CargoDetallePage from "./containers/pages/CargoDetallePage.jsx";
import CrearCargoPage from "./containers/pages/CrearCargoPage.jsx";
import ServiciosPreciosPage from "./containers/pages/ServiciosPreciosPage.jsx";
import CrearServicioPage from "./containers/pages/CrearServicioPage.jsx";
import ServicioDetallePage from "./containers/pages/ServicioDetallePage.jsx";
import LentesPage from "./containers/pages/LentesPage.jsx";
import LenteDetallePage from "./containers/pages/LenteDetallePage.jsx";
import CrearLentePage from "./containers/pages/CrearLentePage.jsx";
import ColegasPage from "./containers/pages/ColegasPage.jsx";
import ColegaDetallePage from "./containers/pages/ColegaDetallePage.jsx";
import CrearColegaPage from "./containers/pages/CrearColegaPage.jsx";
import AseguradorasPage from "./containers/pages/AseguradorasPage.jsx";
import CrearAseguradoraPage from "./containers/pages/CrearAseguradoraPage.jsx";
import AseguradoraDetallePage from "./containers/pages/AseguradoraDetallePage.jsx";
import UsuariosPage from "./containers/pages/UsuariosPage.jsx";
import CrearUsuarioPage from "./containers/pages/CrearUsuarioPage.jsx";
import UsuarioDetallePage from "./containers/pages/UsuarioDetallePage.jsx";
import ConfiguracionPage from "./containers/pages/ConfiguracionPage.jsx";
import MensajesContactoPage from "./containers/pages/MensajesContactoPage.jsx";
import PerfilPage from "./containers/pages/PerfilPage.jsx";
import RequireModulo from "./components/guards/RequireModulo.jsx";

import { ConfigProvider } from "antd";
import { showVisumEffect } from "./components/utils/antWaveEffect";
import store from "./store";
import { isTokenExpired, tokenExpiresInMs } from "./utils/tokenUtils";
import { sessionManager } from "./redux/sessionManager";
import ModalReautenticar from "./components/modals/ModalReautenticar";
import GlobalToastHost from "./components/toasts/GlobalToastHost.jsx";
import { Provider, useDispatch } from "react-redux";
import { PermisosProvider } from "./context/PermisosContext";
import LoginHeader from "./components/navigation/LoginHeader.jsx";
import "./assets/css/bootstrap.css";
import "./styles.css";

// Modulos de negocio: los 9 de Visum ya estan construidos y enrutados aqui.
// Cada ruta de negocio declara `modulo` (llave real de backend/app/models/
// permisos.py) y se envuelve en <RequireModulo> para respetar
// GET /users/me/permisos (ver sincronizar-permisos-modulo).
const routes = [
  { path: "/login", element: <Login /> },
  { path: "/app/home", element: <Home /> },
  { path: "/app/perfil", element: <PerfilPage /> },
  { path: "/app/pacientes", element: <PacientesPage />, modulo: "pacientes" },
  { path: "/app/pacientes/nuevo", accion: "insertar", element: <CrearPacientePage />, modulo: "pacientes" },
  { path: "/app/pacientes/:idPaciente", element: <PacienteDetallePage />, modulo: "pacientes" },
  { path: "/app/pacientes/:idPaciente/editar", accion: "editar", element: <CrearPacientePage />, modulo: "pacientes" },
  { path: "/app/agenda", element: <AgendaPage />, modulo: "agenda" },
  { path: "/app/cargos", element: <CargosPage />, modulo: "cargos_pagos" },
  { path: "/app/cargos/nuevo", accion: "insertar", element: <CrearCargoPage />, modulo: "cargos_pagos" },
  { path: "/app/cargos/:idCargo", element: <CargoDetallePage />, modulo: "cargos_pagos" },
  { path: "/app/servicios", element: <ServiciosPreciosPage />, modulo: "servicios_precios" },
  { path: "/app/servicios/crear", accion: "insertar", element: <CrearServicioPage />, modulo: "servicios_precios" },
  { path: "/app/servicios/:id_servicio", element: <ServicioDetallePage />, modulo: "servicios_precios" },
  { path: "/app/servicios/:id_servicio/editar", accion: "editar", element: <CrearServicioPage />, modulo: "servicios_precios" },
  { path: "/app/lentes", element: <LentesPage />, modulo: "lentes" },
  { path: "/app/lentes/crear", accion: "insertar", element: <CrearLentePage />, modulo: "lentes" },
  { path: "/app/lentes/:idLente", element: <LenteDetallePage />, modulo: "lentes" },
  { path: "/app/lentes/:idLente/editar", accion: "editar", element: <CrearLentePage />, modulo: "lentes" },
  { path: "/app/colegas", element: <ColegasPage />, modulo: "colegas" },
  { path: "/app/colegas/crear", accion: "insertar", element: <CrearColegaPage />, modulo: "colegas" },
  { path: "/app/colegas/:idColega", element: <ColegaDetallePage />, modulo: "colegas" },
  { path: "/app/colegas/:idColega/editar", accion: "editar", element: <CrearColegaPage />, modulo: "colegas" },
  { path: "/app/aseguradoras", element: <AseguradorasPage />, modulo: "aseguradoras" },
  { path: "/app/aseguradoras/crear", accion: "insertar", element: <CrearAseguradoraPage />, modulo: "aseguradoras" },
  { path: "/app/aseguradoras/:id_aseguradora", element: <AseguradoraDetallePage />, modulo: "aseguradoras" },
  { path: "/app/aseguradoras/:id_aseguradora/editar", accion: "editar", element: <CrearAseguradoraPage />, modulo: "aseguradoras" },
  { path: "/app/mensajes", element: <MensajesContactoPage />, modulo: "mensajes_contacto" },
  { path: "/app/usuarios", element: <UsuariosPage />, modulo: "usuarios" },
  { path: "/app/usuarios/crear", accion: "insertar", element: <CrearUsuarioPage />, modulo: "usuarios" },
  { path: "/app/usuarios/:idUser", element: <UsuarioDetallePage />, modulo: "usuarios" },
  { path: "/app/configuracion", element: <ConfiguracionPage />, modulo: "configuracion" },
];

const requireAuth = ({ request }) => {
  const url = new URL(request.url);
  const path = url.pathname.replace(/\/+$/, "") || "/";

  if (path.startsWith("/app")) {
    const token = localStorage.getItem("token");
    if (!token || isTokenExpired(token)) {
      if (token) localStorage.removeItem("token");
      throw redirect("/login");
    }
    return null;
  }

  return null;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <AppShell />,
    loader: requireAuth,
    shouldRevalidate: () => true,
    children: [
      { index: true, loader: () => redirect("/app/home") },
      ...routes.map((route) => ({
        path: route.path,
        element: route.modulo
          ? <RequireModulo modulo={route.modulo} accion={route.accion || "consultar"}>{route.element}</RequireModulo>
          : route.element,
      })),
    ],
  },
]);

function AppShell() {
  useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nodeRef = useRef(null);
  const currentOutlet = useOutlet();
  const pathname = location.pathname;
  const normalpath = pathname.replace(/\/+$/, "") || "/";

  const isAuthPage = normalpath === "/login";
  const showShell = !isAuthPage;

  const [showReauthModal, setShowReauthModal] = useState(false);

  // Guard en cada navegacion: token vigente entra, caducado/ausente va a login.
  useEffect(() => {
    if (isAuthPage) return;
    const t = localStorage.getItem("token");
    if (!t || isTokenExpired(t)) {
      localStorage.removeItem("token");
      navigate("/login", { replace: true });
    }
  }, [normalpath]);

  useEffect(() => {
    const handleSessionExpired = () => setShowReauthModal(true);
    window.addEventListener("session-expired", handleSessionExpired);
    return () => window.removeEventListener("session-expired", handleSessionExpired);
  }, []);

  useEffect(() => {
    const setupTimer = () => {
      const t = localStorage.getItem("token");
      if (!t || isTokenExpired(t)) return undefined;
      const ms = tokenExpiresInMs(t);
      if (ms <= 0) return undefined;
      return setTimeout(() => {
        if (!sessionManager.isRefreshing) {
          sessionManager.setRefreshing(true);
          window.dispatchEvent(new Event("session-expired"));
        }
      }, ms);
    };

    let timerId = setupTimer();
    const resetTimer = () => {
      if (timerId) clearTimeout(timerId);
      timerId = setupTimer();
    };

    window.addEventListener("permisos-refresh", resetTimer);
    return () => {
      if (timerId) clearTimeout(timerId);
      window.removeEventListener("permisos-refresh", resetTimer);
    };
  }, []);

  if (isAuthPage) {
    return (
      <>
        <ModalReautenticar open={showReauthModal} onClose={() => setShowReauthModal(false)} />
        <LoginHeader />
        <div className="login-shell" style={{ marginTop: 44, height: "calc(100vh - 44px)" }}>
          {currentOutlet}
        </div>
      </>
    );
  }

  const topOffset = 44;
  const leftOffset = 56;

  return (
    <>
      <ModalReautenticar open={showReauthModal} onClose={() => setShowReauthModal(false)} />
      {showShell && <AppHeader />}
      <div
        className="content-electron"
        style={{
          marginTop: topOffset,
          minHeight: `calc(100vh - ${topOffset}px)`,
          marginLeft: leftOffset,
        }}
      >
        <SwitchTransition>
          <CSSTransition
            key={location.pathname}
            nodeRef={nodeRef}
            timeout={200}
            classNames="page"
          >
            {() => (
              <div ref={nodeRef} className="page">
                {currentOutlet}
              </div>
            )}
          </CSSTransition>
        </SwitchTransition>
      </div>
    </>
  );
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <Provider store={store}>
    <PermisosProvider>
      <ConfigProvider wave={{ showEffect: showVisumEffect }}>
        <RouterProvider router={router} />
      </ConfigProvider>
      <GlobalToastHost />
    </PermisosProvider>
  </Provider>
);
